import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import ThemeMaker from "../../components/thememaker";
import { config } from "../../utils/config";
import { toast } from "react-toastify";
import { Paginated } from "../../components/Paginated";
import "../../components/table.css";
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import brandStyles from "../Brand/Brand.module.css";
import styles from "./PolicyTemplate.module.css";
import "../../components/mcrbTableTheme.css";
import { renderPolicyPreview, stripHtmlPreview, STATUS_OPTIONS } from "./policyTokens";

const { baseNodeapiUrl } = config;
const termsApiUrl = `${baseNodeapiUrl}/terms`;

const Sstyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: "60%",
    maxWidth: "760px",
    borderRadius: "16px",
    bgcolor: 'background.paper',
    boxShadow: '0 20px 50px rgba(43, 22, 87, 0.24)',
    outline: 'none',
    border: 'none',
    p: 0,
};

function statusPillClass(status: string) {
    if (status === "active") return brandStyles.statusBadgeActive;
    if (status === "inactive") return brandStyles.statusBadgeInactive;
    return styles.statusBadgeDraft;
}

const PolicyTemplateList = () => {
    const [templateList, setTemplateList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState<any>(null);

    const fetchTemplates = async () => {
        setIsLoading(true);
        try {
            const { data } = await axios({
                url: `${termsApiUrl}/all`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
            });
            const list = data?.data?.data || data?.data || data?.terms || (Array.isArray(data) ? data : []);
            setTemplateList(Array.isArray(list) ? list : []);
        } catch (error) {
            console.log("error", error);
            toast.error("Error loading terms templates");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredList = statusFilter
        ? templateList.filter((item: any) => String(item?.status || "draft").toLowerCase() === statusFilter)
        : templateList;

    async function deleteTemplate(id: any) {
        if (!window.confirm("Delete this terms template? This cannot be undone.")) return;
        try {
            await axios({
                url: `${termsApiUrl}/${id}`,
                method: "DELETE",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
            });
            setTemplateList((prev) => prev.filter((item: any) => item.id !== id));
            toast.success("Terms template deleted");
        } catch (error) {
            console.log("error", error);
            toast.error("Something went wrong");
        }
    }

    async function quickStatusChange(id: any, newStatus: string) {
        const prevList = templateList;
        setTemplateList((list) => list.map((item: any) => (item.id === id ? { ...item, status: newStatus } : item)));
        try {
            await axios({
                url: `${termsApiUrl}/${id}`,
                method: "PUT",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
                data: { status: newStatus },
            });
            toast.success("Status updated");
        } catch (error) {
            console.log("error", error);
            setTemplateList(prevList);
            toast.error("Could not update status");
        }
    }

    function openPreview(item: any) {
        setPreviewItem(item);
        setPreviewOpen(true);
    }

    function closePreview() {
        setPreviewOpen(false);
        setPreviewItem(null);
    }

    const COLUMNS = [
        {
            Header: "Sn No.",
            Footer: "Sn No.",
            accessor: (row: any, count: any) => <>{count + 1}</>,
            disableFilters: true,
            sticky: "left",
        },
        {
            Header: "Content",
            Footer: "Content",
            accessor: (row: any) => (
                <span className={styles.contentPreview}>{stripHtmlPreview(row?.content) || "-"}</span>
            ),
            sticky: "left",
        },
        {
            Header: "Variables",
            Footer: "Variables",
            accessor: (row: any) => <span className={styles.varsCount}>{row?.sample_variables?.length || 0}</span>,
            sticky: "left",
        },
        {
            Header: "Status",
            Footer: "Status",
            accessor: (row: any) => (
                <select
                    className={styles.statusSelect}
                    value={row?.status || "draft"}
                    onChange={(e) => quickStatusChange(row.id, e.target.value)}
                >
                    {STATUS_OPTIONS.map((s) => (
                        <option value={s} key={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                </select>
            ),
            sticky: "left",
        },
        {
            Header: "Action",
            Footer: "Action",
            accessor: (row: any) => (
                <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                    <span className={brandStyles.iconBtn} onClick={() => openPreview(row)}>
                        <VisibilityOutlinedIcon style={{ color: "#4c2fb3" }} titleAccess="Preview" />
                    </span>
                    <Link to={`/policytemplate/${row?.id}`} className={brandStyles.iconBtn}>
                        <EditNoteRoundedIcon style={{ color: "#4c2fb3" }} titleAccess="Edit" />
                    </Link>
                    <span className={brandStyles.iconBtn} onClick={() => deleteTemplate(row?.id)}>
                        <DeleteIcon style={{ color: "#EF4444", fontSize: 20 }} titleAccess="Delete" />
                    </span>
                </div>
            ),
            sticky: "left",
        },
    ];

    return (
        <ThemeMaker>
            <div className={`${brandStyles.pageWrap} mcrb-page`}>
                <div className={`${brandStyles.cardOuter} mcrb-card`}>
                    <div className={brandStyles.headerBar}>
                        <span className={brandStyles.title}>Terms &amp; Conditions Templates</span>
                        <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                            <div className={styles.filterRow}>
                                <select className={brandStyles.input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="">All Status</option>
                                    {STATUS_OPTIONS.map((s) => (
                                        <option value={s} key={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                    ))}
                                </select>
                            </div>
                            <Link to="/policytemplate" className={brandStyles.primaryBtn}>
                                + Add Terms Template
                            </Link>
                        </div>
                    </div>

                    <div className={`${brandStyles.bodyArea} ${brandStyles.tableWrap}`} style={{ minHeight: "60vh" }}>
                        {isLoading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
                                <CircularProgress />
                            </div>
                        ) : (
                            <Paginated data={filteredList} columns={COLUMNS} />
                        )}
                    </div>
                </div>
            </div>

            <Modal
                open={previewOpen}
                onClose={closePreview}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={Sstyle} className={styles.modalBox}>
                    <div className={styles.modalHeader}>
                        <p className={styles.modalTitle}>Terms Preview{previewItem?.id ? ` — #${previewItem.id}` : ""}</p>
                        <span className={styles.modalClose}><CloseIcon onClick={closePreview} /></span>
                    </div>
                    <div
                        className={styles.modalBody}
                        dangerouslySetInnerHTML={{ __html: renderPolicyPreview(previewItem?.content || "", previewItem?.sample_variables || []) }}
                    />
                </Box>
            </Modal>
        </ThemeMaker>
    );
};

export default PolicyTemplateList;
